import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalActions,
  SearchSelect,
  Switch,
} from '@evogard/ui';
import type { TenantUser } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';

type Catalog = Array<{
  resource: string;
  label: string;
  actions: Array<{ action: string; label: string }>;
}>;

/**
 * Exceções individuais de permissão, em modal — a exceção de `DESIGN.md`:
 * parte de uma LINHA da tabela de usuários, o formulário tem três campos
 * (recurso, ação, estado) e não há tela de detalhe do usuário para onde
 * "esgotar o progressivo" antes de recorrer ao modal.
 *
 * ## Um par por vez, não um lote
 *
 * `setUserPermissionException` já é o estado final de UM par recurso×ação
 * (`status: null` remove) — cada clique, adicionar ou remover, grava na hora.
 * Um "Salvar" em lote fingiria uma diferença para calcular, e criaria uma
 * segunda verdade sobre quais exceções a pessoa tem enquanto o modal está
 * aberto.
 *
 * `user` vem da linha da tabela — a lista (`['plataforma', 'usuarios']`) é
 * invalidada a cada gravação, e o chamador (`UsersScreen`) deriva o usuário
 * pelo id a cada render, nunca de uma cópia capturada na abertura. Sem isso, o
 * modal mostraria a exceção recém-adicionada só depois de fechar e reabrir.
 */
export function UserPermissionExceptionsModal({
  user,
  catalog,
  onClose,
}: {
  user: TenantUser;
  catalog: Catalog;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [resource, setResource] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState<'permitir' | 'negar'>('permitir');
  const [error, setError] = useState<string | null>(null);

  const set = useMutation({
    /*
     * `act` não vai para a API: ela só conhece o estado final do par. Ele diz
     * QUAL botão gira — sem ele, trocar o estado de uma exceção acendia o giro
     * de "Adicionar exceção", porque os dois mandam `status` não nulo.
     */
    mutationFn: async ({
      act: _act,
      ...body
    }: {
      act: 'adicionar' | 'trocar' | 'remover';
      resource: string;
      action: string;
      status: 'permitir' | 'negar' | null;
    }) => {
      const r = await api.platform.setUserPermissionException({
        params: { id: user.id },
        body,
      });
      return ok(r, 200, 'Não foi possível gravar a exceção.');
    },
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const chosenResource = catalog.find((r) => r.resource === resource);

  /** O giro mora no botão do ato pedido, e só na linha da exceção dele. */
  const isBusy = (e: { resource: string; action: string }, act: 'trocar' | 'remover') =>
    set.isPending &&
    set.variables?.act === act &&
    set.variables.resource === e.resource &&
    set.variables.action === e.action;

  /** Rótulo do catálogo quando ele já carregou; o par cru, sem adivinhar, senão. */
  const describe = (e: { resource: string; action: string }) => {
    const r = catalog.find((c) => c.resource === e.resource);
    const a = r?.actions.find((c) => c.action === e.action);
    return r && a ? `${r.label} — ${a.label}` : `${e.resource}:${e.action}`;
  };

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={`Exceções de ${user.nome}`}
      descricao="Permitir ou negar uma ação específica — sempre vence o que o grupo dela diz."
      size="lg"
    >
      <div className="flex flex-col gap-5">
        {error && <Alert tone="erro">{error}</Alert>}

        <section className="flex flex-col gap-2">
          <h3 className="rotulo text-texto-suave">Exceções atuais</h3>
          {user.permissionExceptions.length === 0 ? (
            <p className="text-sm text-texto-suave">
              Nenhuma — {user.nome.split(' ')[0]} herda só o que o grupo concede.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {user.permissionExceptions.map((e) => (
                <li
                  key={`${e.resource}:${e.action}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-pequeno border border-borda-suave px-3 py-2"
                >
                  {/* `min-w-0` + quebra: o rótulo da ação é uma frase, e sem isto empurra o botão para fora do modal. */}
                  <span className="flex min-w-0 items-start gap-2 text-sm">
                    <Badge tone={e.status === 'permitir' ? 'sucesso' : 'erro'}>
                      {e.status === 'permitir' ? 'Permitido' : 'Negado'}
                    </Badge>
                    <span className="min-w-0 break-words">{describe(e)}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {/*
                     * Editar uma exceção é trocar o estado dela. O par
                     * recurso×ação É a exceção — mudar o recurso seria outra
                     * exceção, e isso é remover esta e adicionar aquela. A rota
                     * grava o estado final do par, então trocar é uma gravação
                     * só, sem remover antes.
                     */}
                    <Button
                      variant="contorno"
                      size="sm"
                      loading={isBusy(e, 'trocar')}
                      disabled={set.isPending}
                      onClick={() =>
                        set.mutate({
                          act: 'trocar',
                          resource: e.resource,
                          action: e.action,
                          status: e.status === 'permitir' ? 'negar' : 'permitir',
                        })
                      }
                    >
                      {e.status === 'permitir'
                        ? 'Trocar para negado'
                        : 'Trocar para permitido'}
                    </Button>
                    <Button
                      variant="discreto"
                      size="sm"
                      loading={isBusy(e, 'remover')}
                      disabled={set.isPending}
                      onClick={() =>
                        set.mutate({
                          act: 'remover',
                          resource: e.resource,
                          action: e.action,
                          status: null,
                        })
                      }
                    >
                      Remover
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-3 border-t border-borda-suave pt-4">
          <h3 className="rotulo text-texto-suave">Adicionar exceção</h3>
          {catalog.length === 0 ? (
            <p className="text-sm text-texto-suave">O catálogo não carregou.</p>
          ) : (
            <>
              {/*
               * `SearchSelect`, e não o `Select` nativo, nos dois campos. O
               * catálogo tem dezenas de recursos e o rótulo de uma ação é uma
               * frase inteira: no nativo não havia como buscar, e o `<option>`
               * não quebra linha — a lista abria mais larga que o modal e, no
               * telefone, a roda cortava a frase que diz o que a ação concede.
               */}
              {/*
               * Recurso e ação lado a lado, alinhados pelo topo: a ação carrega
               * a ajuda "escolha o recurso primeiro" embaixo, e pela base o
               * campo dela subiria fora da linha do outro.
               */}
              <div className="grid items-start gap-3 sm:grid-cols-2">
                <SearchSelect
                  label="Recurso"
                  placeholder="Escolha o recurso…"
                  searchPlaceholder="Buscar recurso…"
                  value={resource}
                  onChange={(next) => {
                    setResource(next);
                    setAction('');
                  }}
                  options={catalog.map((r) => ({ value: r.resource, label: r.label }))}
                />
                <SearchSelect
                  label="Ação"
                  placeholder="Escolha a ação…"
                  searchPlaceholder="Buscar ação…"
                  value={action}
                  disabled={!chosenResource}
                  onChange={setAction}
                  help={!chosenResource ? 'Escolha o recurso primeiro.' : undefined}
                  options={(chosenResource?.actions ?? []).map((a) => ({
                    value: a.action,
                    label: a.label,
                  }))}
                />
              </div>
              {/*
               * O estado e o botão numa linha só, pela base: o botão fechava a
               * seção sozinho numa linha à direita, longe do que ele grava.
               */}
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1.5">
                  {/* `text-xs`, como o rótulo de todo campo da casa — era `text-sm`, e destoava dos dois acima */}
                  <span className="text-xs font-medium text-texto">Estado</span>
                  <Switch
                    label="Permitir ou negar"
                    checked={status === 'permitir'}
                    onCheckedChange={(checked) =>
                      setStatus(checked ? 'permitir' : 'negar')
                    }
                    onLabel="Permitir"
                    offLabel="Negar"
                  />
                </div>

                <Button
                  size="sm"
                  disabled={!resource || !action}
                  loading={set.isPending && set.variables?.act === 'adicionar'}
                  onClick={() => {
                    set.mutate({ act: 'adicionar', resource, action, status });
                    setResource('');
                    setAction('');
                  }}
                >
                  Adicionar exceção
                </Button>
              </div>
              {/* fora da linha de cima: dentro dela, o botão alinhava com esta frase, e não com o seletor */}
              <p className="-mt-1.5 text-xs text-texto-suave">
                {status === 'permitir'
                  ? 'Concede a ação mesmo se o grupo negar.'
                  : 'Tira a ação mesmo se o grupo conceder.'}
              </p>
            </>
          )}
        </section>

        <ModalActions className="border-t border-borda-suave pt-4">
          <Button variant="discreto" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
